/**
 * Two read-only parts of Access controls, drawn from the policy: Remote control (the SSH
 * equivalent: which machines may reach a machine's control port) and Definitions (groups, tags,
 * hosts, device posture and node attributes, as the rules use them).
 */

import { portAllowed, type MeshGrant, type MeshNode, type MeshPolicy } from '@aioe/shared';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { SelectorChip } from './meshSelectors.tsx';
import { requirementsInWords } from './meshWords.ts';
import { ErrorNote, ListBlock, ListGroup, type ArtTone } from './ui.tsx';

/** The port a workbench's or node's control endpoint listens on. */
const CONTROL_PORT = 7433;

export function RemoteControlPanel({ tone: _tone, draft, grants, nodes }: { tone: ArtTone; draft: MeshPolicy; grants: readonly MeshGrant[]; nodes: readonly MeshNode[] }) {
  const reaching = grants.filter((g) => portAllowed(CONTROL_PORT, g.ports ?? []));
  const count = reaching.length + (draft.operatorsReachAll ? 1 : 0) + (draft.ownerReachesOwnNodes ? 1 : 0);
  return (
    <ListGroup title="Remote control" count={count} description="Who may reach a machine's control port, which carries remote control, the screen view and the node host link."><ListBlock flush>
      <ul className="divide-y divide-separator text-sm" role="list">
        {draft.ownerReachesOwnNodes && <Row title="Owners" body="Each person's machines reach one another." />}
        {draft.operatorsReachAll && <Row title="Operators and the platform gateway" body="Operators', administrators' and the platform gateway's machines reach every machine." />}
        {reaching.map((g, i) => (
          <li key={i} className="grid gap-1 px-4 py-2.5 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center md:gap-4">
            <span className="flex flex-wrap gap-1">{g.src.map((s) => <SelectorChip key={s} sel={s} nodes={nodes} />)}</span>
            <span className="text-xs text-muted">may control</span>
            <span className="flex flex-wrap gap-1">{g.dst.map((s) => <SelectorChip key={s} sel={s} nodes={nodes} />)}</span>
          </li>
        ))}
        {count === 0 && <li className="px-4 py-3 text-muted">No rule lets a machine reach another's control port. Remote control still goes through the relay.</li>}
      </ul>
      <p className="border-t border-separator px-4 py-3 text-xs text-muted">Read-only: these are the access rules that open the control port ({CONTROL_PORT}); change them under General access rules. Every request is signed by the platform whichever way it travels. A shell on a machine is not offered, by design.</p>
    </ListBlock></ListGroup>
  );
}

function Row({ title, body }: { title: string; body: string }) {
  return (
    <li className="px-4 py-2.5">
      <span className="block font-medium">{title}</span>
      <span className="block text-xs text-muted">{body}</span>
    </li>
  );
}

const AUTOGROUPS: Array<[string, string]> = [
  ['autogroup:admins', 'Everyone with the administrator role'],
  ['autogroup:operators', 'Everyone with the operator role'],
  ['autogroup:members', 'Every signed-in person'],
  ['autogroup:owner', "Each machine's owner, for their own machines"],
];

export function DefinitionsPanel({ tone: _tone, draft, grants, nodes }: { tone: ArtTone; draft: MeshPolicy; grants: readonly MeshGrant[]; nodes: readonly MeshNode[] }) {
  const defs = useLoad(() => routes.meshDefinitions(), []);
  const owners = draft.tagOwners ?? {};
  const inUse = [...new Set(nodes.flatMap((n) => n.tags))].sort();
  const tags = [...new Set([...Object.keys(owners), ...inUse])].sort();
  const postures = grants.filter((g) => requirementsInWords(g.srcPosture).length > 0);
  const who = (id: string) => defs.data?.people.find((p) => p.id === id);
  return (
    <div className="grid gap-4">
      <ErrorNote error={defs.error} />
      <ListGroup title="Groups" count={(defs.data?.groups.length ?? 0) + AUTOGROUPS.length} description="Groups come from your directory; the role groups are kept by the platform."><ListBlock flush>
        <ul className="divide-y divide-separator text-sm" role="list">
          {AUTOGROUPS.map(([id, text]) => (
            <li key={id} className="flex flex-wrap items-baseline justify-between gap-x-4 px-4 py-2">
              <span className="mono">{id}</span>
              <span className="text-xs text-muted">{text}</span>
            </li>
          ))}
          {defs.data?.groups.map((g) => (
            <li key={g.id} className="px-4 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                <span className="mono">group:{g.id}</span>
                <span className="text-xs text-muted">
                  {g.members.length} {g.members.length === 1 ? 'person' : 'people'} with machines · {g.grants} {g.grants === 1 ? 'rule' : 'rules'}
                  {g.tests ? ` · ${g.tests} ${g.tests === 1 ? 'test' : 'tests'}` : ''}
                </span>
              </div>
              {g.members.length > 0 && <div className="truncate text-xs text-muted">{g.members.map((m) => who(m)?.name ?? who(m)?.email ?? m).join(', ')}</div>}
            </li>
          ))}
          {defs.data && defs.data.groups.length === 0 && <li className="px-4 py-2 text-xs text-muted">No rule or test names a directory group yet.</li>}
        </ul>
      </ListBlock></ListGroup>
      <ListGroup title="Tags" count={tags.length} description="Who may give each tag, and the machines carrying it."><ListBlock flush>
        <ul className="divide-y divide-separator text-sm" role="list">
          {tags.map((t) => (
            <li key={t} className="flex flex-wrap items-baseline justify-between gap-x-4 px-4 py-2">
              <span className="mono">tag:{t}</span>
              <span className="text-xs text-muted">
                {owners[t]?.length ? `${['administrators', ...owners[t]!.map((o) => (o === 'approver' ? 'node approvers' : 'operators'))].join(', ')} may give it` : 'Administrators only'} · {nodes.filter((n) => n.tags.includes(t)).length} machines
              </span>
            </li>
          ))}
          {tags.length === 0 && <li className="px-4 py-2 text-xs text-muted">No tags yet.</li>}
        </ul>
      </ListBlock></ListGroup>
      <ListGroup title="Hosts and IP sets" description="Not needed on the mesh."><ListBlock flush>
        <p className="px-4 py-3 text-sm text-muted">Names and tags replace addresses: a rule picks machines by who owns them, their tags or their kind, so an address list never has to be kept in step with the machines.</p>
      </ListBlock></ListGroup>
      <ListGroup title="Device posture" count={postures.length} description="Requirements a rule puts on the machine it starts from."><ListBlock flush>
        <ul className="divide-y divide-separator text-sm" role="list">
          {postures.map((g, i) => (
            <li key={i} className="px-4 py-2">
              <span className="block truncate">{g.name ?? `${g.src.join(', ')} to ${g.dst.join(', ')}`}</span>
              <span className="block text-xs text-muted">{requirementsInWords(g.srcPosture).join(', ')}</span>
            </li>
          ))}
          {postures.length === 0 && <li className="px-4 py-2 text-xs text-muted">No rule requires device posture. Add a requirement to a rule under General access rules.</li>}
        </ul>
      </ListBlock></ListGroup>
      <ListGroup title="Node attributes" description="What a rule can pick a machine by."><ListBlock flush>
        <p className="px-4 py-3 text-sm text-muted">Rules pick machines by owner, group, tag, kind (workbench, headless node, phone, gateway) and name, and a source machine by the posture its device reports. These are all fixed by the platform; there is nothing to define.</p>
      </ListBlock></ListGroup>
    </div>
  );
}
